import React, { useState, useEffect } from "react";
import editimg from "../../assets/img/edit.svg";
import deleteimg from "../../assets/img/delete.svg";
import searchimg from "../../assets/img/search.svg";
import downloadimg from "../../assets/img/download.svg";
import plusimg from "../../assets/img/plus.svg";
import chevrondown from "../../assets/img/chevron-down.svg";
import threedots from "../../assets/img/three-dots-vertical.svg";
import chevronup from "../../assets/img/chevron-up.svg";
import eyeimg from "../../assets/img/eye.svg";
import checksquareimg from "../../assets/img/check-square.svg";
import subtractimg from "../../assets/img/subtract-modified.svg";
import { fetchApi } from "../../api";
import { fetchHrisEmployeeData } from "../../hrisApi";
import * as XLSX from "xlsx";
import kmsClientEncryption from "../../utils/kmsClientEncryption";
import { useUser } from "../../context/UserContext";

function UserMaster() {
  const { role } = useUser();

  const godrejStyles = {
    primary: "#810055",
    secondary: "#707070",
    font: "GB-BodyCopy",
  };

  const [users, setUsers] = useState([]);
  const [roleNames, setRoleNames] = useState([]);
  const [roleData, setRoleData] = useState([]);
  const [userNameMap, setUserNameMap] = useState({});

  const loggedInEmail = sessionStorage.getItem("userEmail") || "";
  const loggedInUserRecord = users.find(u => u.um_email?.toLowerCase() === loggedInEmail.toLowerCase());
  const isAdmin = role === "admin" || role === "Admin" || loggedInUserRecord?.um_role_name === "Admin" || loggedInUserRecord?.um_role_name === "admin";

  // Role code to description lookup map
  const roleDescMap = {};
  roleData.forEach(r => { roleDescMap[r.rm_role] = r.rm_role_description || r.rm_role; });
  const getRoleDescription = (roleCode) => roleDescMap[roleCode] || roleCode || "";

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await fetchApi("/api/usermaster/all");
        const userArray = Array.isArray(data) ? data : data.users || data.data || [];
        const formattedData = userArray.map((item, index) => ({ srno: index + 1, ...item }));
        setUsers(formattedData);
        const map = {};
        userArray.forEach(u => {
          if (u.um_email && u.um_user_name) map[u.um_email.toLowerCase()] = u.um_user_name;
        });
        setUserNameMap(map);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      }
    };
    const fetchRoles = async () => {
      try {
        const data = await fetchApi("/api/rolemaster/all");
        const roleArray = Array.isArray(data) ? data : data.roles || data.data || [];
        const activeRoles = roleArray.filter(r => r.rm_status === "Active");
        setRoleNames(activeRoles.map(r => r.rm_role));
        setRoleData(activeRoles);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };
    fetchUsers();
    fetchRoles();
  }, []);

  const [formData, setFormData] = useState({
    um_status: "Active",
    um_salary_code: "",
    um_user_name: "",
    um_user_id: "",
    um_email: "",
    um_mobile_no: "",
    um_telephone_no: "",
    um_employee_band: "",
    um_designation: "",
    um_department: "",
    um_role_name: "",
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isHrisValidated, setIsHrisValidated] = useState(false);
  const [showRolePopup, setShowRolePopup] = useState(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [selectedRoleForPopup, setSelectedRoleForPopup] = useState(null);

  // Role popup table state
  const [rolePopupSort, setRolePopupSort] = useState({ key: null, direction: "asc" });
  const [rolePopupPage, setRolePopupPage] = useState(0);
  const [rolePopupPageSize, setRolePopupPageSize] = useState(10);
  const [rolePopupFilter, setRolePopupFilter] = useState(null);
  const [rolePopupFilterDraft, setRolePopupFilterDraft] = useState({ type: "Contains", value: "" });
  const [rolePopupColumnFilters, setRolePopupColumnFilters] = useState({});
  const [hoveredRoleCol, setHoveredRoleCol] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editOriginal, setEditOriginal] = useState({});
  const [editFormSnapshot, setEditFormSnapshot] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showConfirmUpdate, setShowConfirmUpdate] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);

  // Column filter state
  const [columnFilters, setColumnFilters] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterDraft, setFilterDraft] = useState({ type: "Contains", value: "" });

  const updateFilterLive = (colKey, draft) => {
    setFilterDraft(draft);
  };

  // Column resize state
  const [columnWidths, setColumnWidths] = useState({});
  const resizingCol = React.useRef(null);
  const startX = React.useRef(0);
  const startWidth = React.useRef(0);

  const handleMouseDown = (e, colKey) => {
    e.preventDefault();
    resizingCol.current = colKey;
    startX.current = e.clientX;
    const th = e.target.parentElement;
    startWidth.current = th.offsetWidth;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!resizingCol.current) return;
    const diff = e.clientX - startX.current;
    const newWidth = Math.max(50, startWidth.current + diff);
    setColumnWidths(prev => ({ ...prev, [resizingCol.current]: newWidth }));
  };

  const handleMouseUp = () => {
    resizingCol.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [hoveredCol, setHoveredCol] = useState(null);

  const handleSort = (colKey) => {
    setSortConfig(prev => ({
      key: colKey,
      direction: prev.key === colKey && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const columns = [
    { key: "um_status", label: "Status", width: "120px" },
    { key: "um_user_id", label: "User ID", width: "150px" },
    { key: "um_user_name", label: "User Name", width: "200px" },
    { key: "um_salary_code", label: "Salary Code", width: "150px" },
    { key: "um_role_name", label: "Role", width: "150px" },
    { key: "um_email", label: "Email ID", width: "150px" },
    { key: "um_mobile_no", label: "Mobile No.", width: "150px" },
    { key: "um_telephone_no", label: "Telephone No.", width: "150px" },
    { key: "um_employee_band", label: "Employee Band", width: "150px" },
    { key: "um_designation", label: "Designation", width: "150px" },
    { key: "um_department", label: "Department", width: "150px" },
    { key: "um_inserted_by", label: "Inserted By", width: "150px" },
    { key: "um_inserted_on", label: "Inserted On", width: "150px" },
    { key: "um_modified_by", label: "Modified By", width: "150px" },
    { key: "um_modified_on", label: "Modified On", width: "150px" },
  ];

  const applyColumnFilter = (colKey) => {
    if (filterDraft.value || filterDraft.type === "Blank" || filterDraft.type === "Not Blank") {
      setColumnFilters(prev => ({ ...prev, [colKey]: { ...filterDraft } }));
    }
    setActiveFilter(null);
    setCurrentPage(0);
  };

  const resetColumnFilter = (colKey) => {
    setColumnFilters(prev => { const n = { ...prev }; delete n[colKey]; return n; });
    setFilterDraft({ type: "Contains", value: "" });
    setActiveFilter(null);
    setCurrentPage(0);
  };

  const matchesFilter = (val, filter) => {
    const cellVal = String(val || "").toLowerCase();
    const filterVal = (filter.value || "").toLowerCase();
    switch (filter.type) {
      case "Contains": return cellVal.includes(filterVal);
      case "Not Contains": return !cellVal.includes(filterVal);
      case "Equals": return cellVal === filterVal;
      case "Not Equals": return cellVal !== filterVal;
      case "Starts With": return cellVal.startsWith(filterVal);
      case "Ends With": return cellVal.endsWith(filterVal);
      case "Blank": return cellVal.trim() === "";
      case "Not Blank": return cellVal.trim() !== "";
      default: return true;
    }
  };

  const maskPII = (value) => {
    if (!value || typeof value !== 'string' || value.length < 6) return value;
    const visible = 4;
    return value.slice(0, -visible).replace(/[0-9]/g, '*') + value.slice(-visible);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  const resolveUserName = (value) => {
    if (!value) return "";
    if (value.includes("@")) return userNameMap[value.toLowerCase()] || value;
    return value;
  };

  const getCellValue = (user, key) => {
    if (key === "um_inserted_on" || key === "um_modified_on") {
      return formatDateTime(user[key]);
    }
    if (key === "um_mobile_no" || key === "um_telephone_no") {
      return maskPII(user[key]);
    }
    if (key === "um_email") {
      return user[key] || "";
    }
    if (key === "um_role_name") {
      return getRoleDescription(user[key]);
    }
    if (key === "um_inserted_by" || key === "um_modified_by") {
      return resolveUserName(user[key]);
    }
    return user[key] || "";
  };

  const filteredUsers = users.filter(user => {
    // Global search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matches = [
        user.um_status, user.um_salary_code, user.um_user_name,
        user.um_user_id, user.um_email, user.um_mobile_no, user.um_telephone_no,
        user.um_employee_band, user.um_designation,
        user.um_department, user.um_role_name
      ].some(val => String(val || "").toLowerCase().includes(term));
      if (!matches) return false;
    }
    // Column filters
    for (const [colKey, filter] of Object.entries(columnFilters)) {
      if (!matchesFilter(getCellValue(user, colKey), filter)) return false;
    }
    return true;
  }).sort((a, b) => {
    if (!sortConfig.key) {
      if (a.um_status !== b.um_status) {
        return a.um_status === 'Active' ? -1 : 1;
      }
      const aTime = new Date(a.um_modified_on || a.um_inserted_on || 0).getTime();
      const bTime = new Date(b.um_modified_on || b.um_inserted_on || 0).getTime();
      return bTime - aTime;
    }
    const aVal = String(getCellValue(a, sortConfig.key) || "").toLowerCase();
    const bVal = String(getCellValue(b, sortConfig.key) || "").toLowerCase();
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const numberOfPages = () => Math.ceil(filteredUsers.length / pageSize);

  const hrisTimeout = React.useRef(null);

  const hrisAutoFields = ['um_user_name', 'um_salary_code', 'um_email', 'um_employee_band', 'um_designation', 'um_department'];

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    
    if (id === 'um_user_id') {
      const textValue = value.replace(/[^a-zA-Z]/g, '');
      setFormData(prev => ({ ...prev, [id]: textValue, um_email: '', um_user_name: '', um_salary_code: '', um_employee_band: '', um_designation: '', um_department: '' }));
      setIsHrisValidated(false);
      setValidationErrors(prev => { const n = { ...prev }; delete n.um_user_id; return n; });

      if (hrisTimeout.current) clearTimeout(hrisTimeout.current);
      if (textValue.trim()) {
        hrisTimeout.current = setTimeout(async () => {
          try {
            const hrisData = await fetchHrisEmployeeData(textValue);
            if (hrisData && hrisData.Name) {
              setFormData(prev => ({
                ...prev,
                um_user_id: textValue,
                um_email: `${textValue}@godrej.com`,
                um_user_name: hrisData.Name || '',
                um_salary_code: hrisData.SalaryCode || '',
                um_employee_band: hrisData.Band || '',
                um_designation: hrisData.DesignationCode || '',
                um_department: hrisData.Division || '',
              }));
              setIsHrisValidated(true);
              setValidationErrors(prev => { const n = { ...prev }; delete n.um_user_id; return n; });
            } else {
              setIsHrisValidated(false);
              setValidationErrors(prev => ({ ...prev, um_user_id: 'Enter correct User ID' }));
            }
          } catch {
            setIsHrisValidated(false);
            setValidationErrors(prev => ({ ...prev, um_user_id: 'Enter correct User ID' }));
          }
        }, 600);
      }
    } else if (hrisAutoFields.includes(id)) {
      // Read-only fields populated by HRIS - do not allow manual edit
      return;
    } else if (id === 'um_mobile_no') {
      const allowed = value.replace(/[^0-9+\-]/g, '').slice(0, 18);
      setFormData(prev => ({ ...prev, [id]: allowed }));
    } else if (id === 'um_telephone_no') {
      const allowed = value.replace(/[^0-9+\-]/g, '').slice(0, 23);
      setFormData(prev => ({ ...prev, [id]: allowed }));
    } else {
      setFormData(prev => ({ ...prev, [id]: value }));
    }
  };

  const handleRoleSelect = () => {
    if (selectedRoleForPopup) {
      setFormData(prev => ({ ...prev, um_role_name: selectedRoleForPopup }));
      setShowRolePopup(false);
      setRoleSearchTerm("");
      setSelectedRoleForPopup(null);
    }
  };

  const filteredRolesForPopup = roleData.filter(r => {
    if (roleSearchTerm) {
      const term = roleSearchTerm.toLowerCase();
      if (!r.rm_role?.toLowerCase().includes(term) && !r.rm_role_description?.toLowerCase().includes(term)) return false;
    }
    for (const [colKey, filter] of Object.entries(rolePopupColumnFilters)) {
      const val = colKey === "role_code" ? r.rm_role : r.rm_role_description;
      if (!matchesFilter(val, filter)) return false;
    }
    return true;
  }).sort((a, b) => {
    if (!rolePopupSort.key) return 0;
    const aVal = (rolePopupSort.key === "role_code" ? a.rm_role : a.rm_role_description || "").toLowerCase();
    const bVal = (rolePopupSort.key === "role_code" ? b.rm_role : b.rm_role_description || "").toLowerCase();
    if (aVal < bVal) return rolePopupSort.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return rolePopupSort.direction === "asc" ? 1 : -1;
    return 0;
  });

  const rolePopupTotalPages = () => Math.ceil(filteredRolesForPopup.length / rolePopupPageSize);
  const paginatedRoles = filteredRolesForPopup.slice(rolePopupPage * rolePopupPageSize, (rolePopupPage + 1) * rolePopupPageSize);

  const loggedInUser = sessionStorage.getItem("userDisplayName") || sessionStorage.getItem("userEmail") || "";

  const validateForm = () => {
    const errors = {};
    const mandatoryFields = [
      { key: 'um_salary_code', label: 'Salary Code' },
      { key: 'um_user_name', label: 'User Name' },
      { key: 'um_user_id', label: 'User ID' },
      { key: 'um_email', label: 'Email ID' },
      { key: 'um_mobile_no', label: 'Mobile No.' },
      { key: 'um_employee_band', label: 'Employee Band' },
      { key: 'um_designation', label: 'Designation' },
      { key: 'um_department', label: 'Department' },
      { key: 'um_role_name', label: 'Role Name' }
    ];

    mandatoryFields.forEach(field => {
      if (!formData[field.key] || String(formData[field.key]).trim() === '') {
        errors[field.key] = `${field.label} is mandatory`;
      }
    });

    // Block save if user ID is not validated from HRIS
    if (!isEditing && !isHrisValidated) {
      errors.um_user_id = 'Enter correct User ID';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    try {
      // Check for duplicate user when adding new
      if (!isEditing) {
        const duplicate = users.find(u => 
          u.um_user_id?.toLowerCase() === formData.um_user_id?.toLowerCase() || 
          u.um_email?.toLowerCase() === formData.um_email?.toLowerCase() ||
          u.um_salary_code?.toLowerCase() === formData.um_salary_code?.toLowerCase()
        );
        if (duplicate) {
          setSuccessMessage("User already exists.");
          return;
        }
      }
      const now = new Date().toISOString();
      let payload = { ...formData };

      if (isEditing) {
        payload.um_id = editId;
        payload.um_inserted_by = editOriginal.um_inserted_by;
        payload.um_inserted_on = editOriginal.um_inserted_on;
        payload.um_modified_by = loggedInUser;
        payload.um_modified_on = now;
      } else {
        payload.um_inserted_by = loggedInUser;
        payload.um_inserted_on = now;
        payload.um_modified_by = loggedInUser;
        payload.um_modified_on = now;
      }

      // Encrypt PII fields (mobile, telephone numbers, and email) before sending
      const encryptedPayload = await kmsClientEncryption.encryptPIIFields(payload, ["um_mobile_no", "um_telephone_no", "um_email"]);

      if (isEditing) {
        await fetchApi("/api/usermaster/update", {
          method: "PUT",
          body: JSON.stringify(encryptedPayload),
        });
      } else {
        await fetchApi("/api/usermaster/save", {
          method: "POST",
          body: JSON.stringify(encryptedPayload),
        });
      }

      // Re-fetch to get latest data from DB
      const data = await fetchApi("/api/usermaster/all");
      const userArray = Array.isArray(data) ? data : data.users || data.data || [];
      setUsers(userArray.map((item, index) => ({ srno: index + 1, ...item })));

      // Show success message
      if (isEditing) {
        setSuccessMessage("User Edited Successfully.");
      } else {
        setSuccessMessage("New User Created Successfully.");
      }

      resetForm();
      setShowForm(false);
    } catch (error) {
      console.error("Error saving user:", error);
      alert("Failed to save user. Please try again.");
    }
  };

  const resetForm = () => {
    setFormData({ 
      um_status: "Active",
      um_salary_code: "",
      um_user_name: "",
      um_user_id: "",
      um_email: "",
      um_mobile_no: "",
      um_telephone_no: "",
      um_employee_band: "",
      um_designation: "",
      um_department: "",
      um_role_name: "",
    });
    setValidationErrors({});
    setIsHrisValidated(false);
    setShowRolePopup(false);
    setRoleSearchTerm("");
    setSelectedRoleForPopup(null);
    setIsEditing(false);
    setEditId(null);
    setViewId(null);
  };

  const handleEdit = async (user) => {
    try {
      // Fetch edit-specific data with decrypted but masked phone numbers and original email
      const editData = await fetchApi(`/api/usermaster/edit/${user.um_id}`);
      const userData = editData?.data || editData;
      
      if (userData && !editData?.didError) {
        setFormData({
          um_status: userData.um_status || user.um_status,
          um_salary_code: userData.um_salary_code || user.um_salary_code,
          um_user_name: userData.um_user_name || user.um_user_name,
          um_user_id: userData.um_user_id || user.um_user_id || '',
          um_email: userData.um_email || user.um_email,
          um_mobile_no: userData.um_mobile_no || user.um_mobile_no,
          um_telephone_no: userData.um_telephone_no || user.um_telephone_no,
          um_employee_band: userData.um_employee_band || user.um_employee_band,
          um_designation: userData.um_designation || user.um_designation,
          um_department: userData.um_department || user.um_department,
          um_role_name: userData.um_role_name || user.um_role_name,
        });
        setEditFormSnapshot({
          um_status: userData.um_status || user.um_status,
          um_salary_code: userData.um_salary_code || user.um_salary_code,
          um_user_name: userData.um_user_name || user.um_user_name,
          um_user_id: userData.um_user_id || user.um_user_id || '',
          um_email: userData.um_email || user.um_email,
          um_mobile_no: userData.um_mobile_no || user.um_mobile_no,
          um_telephone_no: userData.um_telephone_no || user.um_telephone_no,
          um_employee_band: userData.um_employee_band || user.um_employee_band,
          um_designation: userData.um_designation || user.um_designation,
          um_department: userData.um_department || user.um_department,
          um_role_name: userData.um_role_name || user.um_role_name,
        });
      } else {
        setFormData({
          um_status: user.um_status,
          um_salary_code: user.um_salary_code,
          um_user_name: user.um_user_name,
          um_user_id: user.um_user_id || '',
          um_email: user.um_email,
          um_mobile_no: user.um_mobile_no,
          um_telephone_no: user.um_telephone_no,
          um_employee_band: user.um_employee_band,
          um_designation: user.um_designation,
          um_department: user.um_department,
          um_role_name: user.um_role_name,
        });
        setEditFormSnapshot({
          um_status: user.um_status,
          um_salary_code: user.um_salary_code,
          um_user_name: user.um_user_name,
          um_user_id: user.um_user_id || '',
          um_email: user.um_email,
          um_mobile_no: user.um_mobile_no,
          um_telephone_no: user.um_telephone_no,
          um_employee_band: user.um_employee_band,
          um_designation: user.um_designation,
          um_department: user.um_department,
          um_role_name: user.um_role_name,
        });
      }
    } catch (error) {
      console.error("Error fetching edit data:", error);
      setFormData({
        um_status: user.um_status,
        um_salary_code: user.um_salary_code,
        um_user_name: user.um_user_name,
        um_user_id: user.um_user_id || '',
        um_email: user.um_email,
        um_mobile_no: user.um_mobile_no,
        um_telephone_no: user.um_telephone_no,
        um_employee_band: user.um_employee_band,
        um_designation: user.um_designation,
        um_department: user.um_department,
        um_role_name: user.um_role_name,
      });
      setEditFormSnapshot({
        um_status: user.um_status,
        um_salary_code: user.um_salary_code,
        um_user_name: user.um_user_name,
        um_user_id: user.um_user_id || '',
        um_email: user.um_email,
        um_mobile_no: user.um_mobile_no,
        um_telephone_no: user.um_telephone_no,
        um_employee_band: user.um_employee_band,
        um_designation: user.um_designation,
        um_department: user.um_department,
        um_role_name: user.um_role_name,
      });
    }
    setEditOriginal({ um_inserted_by: user.um_inserted_by, um_inserted_on: user.um_inserted_on });
    setIsEditing(true);
    setIsHrisValidated(true);
    setEditId(user.um_id);
    setShowForm(true);
  };

  // GS-SEC-006: Sanitize cell values to prevent CSV formula injection
  // isPhoneField=true allows + and - at start (valid for phone numbers)
  const sanitizeForCSV = (value, isPhoneField = false) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    if (isPhoneField) {
      if (/^[=@\t\r|]/.test(str)) return "'" + str;
    } else {
      if (/^[=+\-@\t\r|]/.test(str)) return "'" + str;
    }
    return str;
  };

  const handleDownload = async () => {
    if (isAdmin) {
      // Admin: call export API to get fully decrypted unmasked phone numbers,
      // but only export the currently filtered/sorted user IDs
      try {
        const response = await fetchApi(`/api/usermaster/export?userRole=admin`);
        const allExportUsers = Array.isArray(response) ? response : response?.data || response?.users || [];
        
        // Build a set of filtered user IDs to match against export data
        const filteredIds = new Set(filteredUsers.map(u => u.um_id));
        
        // Filter export data to only include currently visible (filtered/sorted) users
        const matchedExport = allExportUsers.filter(u => filteredIds.has(u.um_id));
        
        // Maintain the same sort order as filteredUsers
        const idOrder = filteredUsers.map(u => u.um_id);
        matchedExport.sort((a, b) => idOrder.indexOf(a.um_id) - idOrder.indexOf(b.um_id));
        
        const exportData = matchedExport.map(user => ({
          "Status": sanitizeForCSV(user.um_status),
          "User ID": sanitizeForCSV(user.um_user_id),
          "User Name": sanitizeForCSV(user.um_user_name),
          "Salary Code": sanitizeForCSV(user.um_salary_code),
          "Role": sanitizeForCSV(getRoleDescription(user.um_role_name)),
          "Email ID": sanitizeForCSV(user.um_email),
          "Mobile No.": sanitizeForCSV(user.um_mobile_no, true),
          "Telephone No.": sanitizeForCSV(user.um_telephone_no, true),
          "Employee Band": sanitizeForCSV(user.um_employee_band),
          "Designation": sanitizeForCSV(user.um_designation),
          "Department": sanitizeForCSV(user.um_department),
          "Inserted By": sanitizeForCSV(user.um_inserted_by),
          "Inserted On": sanitizeForCSV(formatDateTime(user.um_inserted_on)),
          "Modified By": sanitizeForCSV(user.um_modified_by),
          "Modified On": sanitizeForCSV(formatDateTime(user.um_modified_on)),
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "UserMaster");
        XLSX.writeFile(wb, "UserMasterData.xlsx");
      } catch (error) {
        console.error("Error downloading data:", error);
      }
    } else {
      // Non-admin: use filteredUsers directly (already decrypted+masked from /all API)
      const exportData = filteredUsers.map(user => ({
        "Status": sanitizeForCSV(user.um_status),
        "User ID": sanitizeForCSV(user.um_user_id),
        "User Name": sanitizeForCSV(user.um_user_name),
        "Salary Code": sanitizeForCSV(user.um_salary_code),
        "Role": sanitizeForCSV(getRoleDescription(user.um_role_name)),
        "Email ID": sanitizeForCSV(user.um_email),
        "Mobile No.": sanitizeForCSV(maskPII(user.um_mobile_no), true),
        "Telephone No.": sanitizeForCSV(maskPII(user.um_telephone_no), true),
        "Employee Band": sanitizeForCSV(user.um_employee_band),
        "Designation": sanitizeForCSV(user.um_designation),
        "Department": sanitizeForCSV(user.um_department),
        "Inserted By": sanitizeForCSV(user.um_inserted_by),
        "Inserted On": sanitizeForCSV(formatDateTime(user.um_inserted_on)),
        "Modified By": sanitizeForCSV(user.um_modified_by),
        "Modified On": sanitizeForCSV(formatDateTime(user.um_modified_on)),
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "UserMaster");
      XLSX.writeFile(wb, "UserMasterData.xlsx");
    }
  };

  const [deleteError, setDeleteError] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [noChangesError, setNoChangesError] = useState(false);

  const handleDelete = async (id) => {
    // Find the user being deleted
    const userToDelete = users.find(u => u.um_id === id);
    if (!userToDelete) return;

    // Check if user has already generated a signature
    try {
      const res = await fetchApi(`/api/signature/check-exists?salaryCode=${encodeURIComponent(userToDelete.um_salary_code)}`);
      if (res && res.exists) {
        setDeleteError("This user is already generated signature and cannot be deleted. Please make it inactive from the Edit option.");
        return;
      }
    } catch (error) {
      console.error("Error checking signature:", error);
    }

    setShowConfirmDelete(id);
  };

  const confirmDelete = async () => {
    const id = showConfirmDelete;
    setShowConfirmDelete(null);
    try {
      await fetchApi(`/api/usermaster/${id}`, { method: "DELETE" });
      setUsers(prev => prev.filter(user => user.um_id !== id));
      setSuccessMessage("User deleted successfully.");
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Failed to delete user. Please try again.");
    }
  };

  const btnStyle = {
    backgroundColor: godrejStyles.primary,
    color: "white",
    border: "none",
    padding: "3px 15px",
    borderRadius: "25px",
  };

  const paginationBtnStyle = (disabled) => ({
    width: "32px", height: "32px", borderRadius: "50%",
    border: "1px solid #ddd", background: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    color: "#810055", fontWeight: "bold",
  });

  return (
    <div style={{ fontFamily: godrejStyles.font, paddingRight: "10px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: "10px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <h1 style={{ fontSize: "18px", fontWeight: "400", color: "#333", margin: "0", marginRight: "15px" }}>User Master</h1>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="btn" style={{...btnStyle, textTransform: 'none', fontSize: '14px', fontWeight: '400'}}>
            <img src={plusimg} alt="Add" style={{ width: '16px', height: '21px', marginRight: '8px', filter: 'brightness(0) invert(1)' }} />
            Add New User
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <input 
            type="text" 
            placeholder="Search here..." 
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
            style={{ 
              width: "400px", 
              height: "35px", 
              padding: "4px 34px", 
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
              backgroundImage: `url(${searchimg})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "10px center",
              backgroundSize: "16px 16px"
            }} 
          />
          <img 
            src={downloadimg} 
            alt="Download" 
            title="Download" 
            onClick={handleDownload}
            style={{ 
              width: "20px", 
              height: "20px", 
              marginLeft: "15px",
              cursor: "pointer"
            }} 
          />
        </div>
      </div>

      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '0',
            borderRadius: '0',
            width: '70%',
            maxWidth: '800px',
            maxHeight: '80%',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #ccc', padding: '20px 20px 15px 20px' }}>
              <h2 style={{ color: '#333', fontSize: '18px', fontWeight: '500', margin: 0 }}>
                {isEditing ? 'Edit User' : 'Add New User'}
              </h2>
              <button 
                onClick={() => { resetForm(); setShowForm(false); }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#707070',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '0' }}>
            <form style={{ padding: '0', margin: '0' }}>
                <div style={{ width: '100%', padding: '20px', marginTop: '10px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', margin: 0, padding: 0 }}>
                    <div style={{ width: '33.33%', padding: '0 10px', paddingBottom: '20px' }}>
                      <label className="form-label" style={{ color: '#707070', marginBottom: '8px', display: 'block' }}>Status</label>
                      <div className="sw-status col">
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '3px' }}>
                          <div className="switch">
                            <input 
                              className="react-switch-checkbox" 
                              id="react-switch-new-undefined" 
                              type="checkbox" 
                              checked={formData.um_status === "Active"}
                              onChange={(e) => { setFormData(prev => ({ ...prev, um_status: e.target.checked ? "Active" : "Inactive" })); }}
                              style={{ display: 'none' }}
                            />
                            <label 
                              className="react-switch-label" 
                              htmlFor="react-switch-new-undefined"
                              style={{
                                width: '29px',
                                height: '13px',
                                backgroundColor: formData.um_status === "Active" ? '#33C037' : '#707070',
                                borderRadius: '13px',
                                position: 'relative',
                                cursor: 'pointer',
                                display: 'inline-block',
                                transition: 'background-color 0.3s',
                                marginTop: '8px'
                              }}
                            >
                              <span 
                                className="react-switch-button undefined"
                                style={{
                                  position: 'absolute',
                                  top: '-1px',
                                  left: formData.um_status === "Active" ? '15px' : '-1px',
                                  width: '15px',
                                  height: '15px',
                                  backgroundColor: '#fff',
                                  border: `1px solid ${formData.um_status === "Active" ? '#33C037' : '#707070'}`,
                                  borderRadius: '50%',
                                  transition: 'left 0.3s, border-color 0.3s'
                                }}
                              ></span>
                            </label>
                          </div>
                          <label className={`fieldLabel ${formData.um_status} form-label`} style={{ marginLeft: '10px', color: '#707070' }}>{formData.um_status}</label>
                        </div>
                      </div>
                    </div>
                    {[
                      ["um_user_id", "User ID"],
                      ["um_user_name", "User Name"],
                      ["um_salary_code", "Salary Code"],
                    ].map(([id, label]) => (
                      <div style={{ width: '33.33%', padding: '0 10px', paddingBottom: '20px' }} key={id}>
                        <input 
                          className="form-control" 
                          id={id} 
                          type="text" 
                          placeholder={label}
                          value={formData[id]} 
                          onChange={handleInputChange}
                          readOnly={isEditing || hrisAutoFields.includes(id)}
                          style={{
                            border: `1px solid ${validationErrors[id] ? '#dc3545' : '#ccc'}`,
                            borderRadius: '4px',
                            padding: '15px 18px',
                            fontSize: '16px',
                            width: '100%',
                            height: '50px',
                            backgroundColor: (isEditing || hrisAutoFields.includes(id)) ? '#f5f5f5' : '#fff'
                          }}
                        />
                        {validationErrors[id] && (
                          <div style={{ 
                            color: '#dc3545', 
                            fontSize: '14px', 
                            marginTop: '4px' 
                          }}>
                            {validationErrors[id]}
                          </div>
                        )}
                      </div>
                    ))}
                    <div style={{ width: '33.33%', padding: '0 10px', paddingBottom: '20px' }}>
                      <div
                        onClick={() => setShowRolePopup(true)}
                        style={{
                          border: `1px solid ${validationErrors.um_role_name ? '#dc3545' : '#ccc'}`,
                          borderRadius: '4px',
                          padding: '12px 18px',
                          fontSize: '16px',
                          width: '100%',
                          height: '50px',
                          color: formData.um_role_name ? '#000' : '#999',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: '#fff'
                        }}
                      >
                        <span>{formData.um_role_name ? getRoleDescription(formData.um_role_name) : 'Role'}</span>
                        <span style={{ display: 'flex', alignItems: 'center', position: 'relative', width: '22px', height: '16px' }}>
                          <img src={subtractimg} alt="open" style={{ width: '14px', height: '14px', position: 'absolute', left: '2px', top: '7px', filter: 'invert(45%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)', clipPath: 'inset(1px 4px 0 0)' }} />
                          <img src={checksquareimg} alt="select" style={{ width: '14px', height: '14px', position: 'absolute', left: '6px', filter: 'invert(45%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)' }} />
                        </span>
                      </div>
                      {validationErrors.um_role_name && (
                        <div style={{ 
                          color: '#dc3545', 
                          fontSize: '14px', 
                          marginTop: '4px' 
                        }}>
                          {validationErrors.um_role_name}
                        </div>
                      )}
                    </div>
                    {[
                      ["um_mobile_no", "Mobile No."],
                      ["um_telephone_no", "Telephone No."],
                      ["um_email", "Email ID"],
                      ["um_employee_band", "Employee Band"],
                      ["um_designation", "Designation"],
                      ["um_department", "Department"],
                    ].map(([id, label]) => {
                      const isReadOnly = isEditing ? !['um_mobile_no', 'um_telephone_no'].includes(id) : hrisAutoFields.includes(id);
                      return (
                      <div style={{ width: '33.33%', padding: '0 10px', paddingBottom: '20px' }} key={id}>
                        <input 
                          className="form-control" 
                          id={id} 
                          type="text" 
                          placeholder={label}
                          value={formData[id]} 
                          onChange={handleInputChange}
                          readOnly={isReadOnly}
                          style={{
                            border: `1px solid ${validationErrors[id] ? '#dc3545' : '#ccc'}`,
                            borderRadius: '4px',
                            padding: '15px 18px',
                            fontSize: '16px',
                            width: '100%',
                            height: '50px',
                            backgroundColor: isReadOnly ? '#f5f5f5' : '#fff'
                          }}
                        />
                        {validationErrors[id] && (
                          <div style={{ 
                            color: '#dc3545', 
                            fontSize: '14px', 
                            marginTop: '4px' 
                          }}>
                            {validationErrors[id]}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem', width: '100%' }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button type="button" className="btn" onClick={() => { resetForm(); setShowForm(false); }} style={{ backgroundColor: 'transparent', color: '#707070', border: '1px solid #ccc', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>Cancel</button>
                      <button type="button" className="btn" onClick={() => { if (isEditing && editFormSnapshot) { setFormData(editFormSnapshot); } else { setFormData({ um_status: "Active", um_salary_code: "", um_user_name: "", um_user_id: "", um_email: "", um_mobile_no: "", um_telephone_no: "", um_employee_band: "", um_designation: "", um_department: "", um_role_name: "" }); } setValidationErrors({}); }} style={{ backgroundColor: 'transparent', color: '#810055', border: 'none', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px' }}>Reset Fields</button>
                    </div>
                    <div>
                      <button type="button" className="btn me-2" onClick={() => { if (!validateForm()) return; if (isEditing) { if (JSON.stringify(formData) === JSON.stringify(editFormSnapshot)) { setNoChangesError(true); return; } setShowConfirmUpdate(true); } else { handleSave(); } }} style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>{isEditing ? "Update" : "Add"}</button>
                    </div>
                  </div>
                </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Role Selection Popup */}
      {showRolePopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '0',
            borderRadius: '0',
            width: '70%',
            maxWidth: '800px',
            height: '80%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #ccc', padding: '20px 20px 15px 20px', flexShrink: 0 }}>
              <h2 style={{ color: '#333', fontSize: '18px', fontWeight: '500', margin: 0 }}>Role Master</h2>
              <button 
                onClick={() => {
                  setShowRolePopup(false);
                  setRoleSearchTerm("");
                  setSelectedRoleForPopup(null);
                  setRolePopupPage(0);
                  setRolePopupSort({ key: null, direction: "asc" });
                  setRolePopupColumnFilters({});
                  setRolePopupFilter(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#707070',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Search Bar */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", paddingLeft: "10px", paddingRight: "20px", paddingTop: "10px", flexShrink: 0 }}>
              <input 
                type="text" 
                placeholder="Search here..." 
                value={roleSearchTerm}
                onChange={(e) => { setRoleSearchTerm(e.target.value); setRolePopupPage(0); }}
                style={{ 
                  width: "250px", 
                  height: "30px", 
                  padding: "4px 30px", 
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "13px",
                  backgroundImage: `url(${searchimg})`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "8px center",
                  backgroundSize: "14px 14px"
                }} 
              />
            </div>

            {/* Table */}
            <div style={{ flex: 1, overflowY: "auto", overflowX: "auto", padding: "0px 10px 10px 10px", boxSizing: "border-box", backgroundColor: "#fff", marginTop: "5px", minHeight: 0 }}>
              {Object.keys(rolePopupColumnFilters).length > 0 && (
                <div className="filterList" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', padding: '5px 0px', marginBottom: '5px' }}>
                  {Object.entries(rolePopupColumnFilters).map(([colKey, filter]) => {
                    const label = colKey === 'role_code' ? 'Role Code' : 'Role Description';
                    return (
                      <div key={colKey} className="filter-col pr-0 me-2 mb-2" style={{ display: 'flex', alignItems: 'center', backgroundColor: '#F9F2F6', borderRadius: '25px', padding: '2px 8px', fontSize: '13px' }}>
                        <span className="ps-2 btn-text">{label} : {filter.type === 'Blank' || filter.type === 'Not Blank' ? filter.type : filter.value}</span>
                        <div onClick={() => { setRolePopupColumnFilters(prev => { const n = { ...prev }; delete n[colKey]; return n; }); setRolePopupPage(0); }} style={{ cursor: 'pointer', marginLeft: '4px' }}>
                          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 16 16" height="25" width="25" xmlns="http://www.w3.org/2000/svg"><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"></path></svg>
                        </div>
                      </div>
                    );
                  })}
                  <div>
                    <button type="button" className="btn" style={{ backgroundColor: 'white', color: '#810055', border: '1px solid #810055', padding: '3px 15px', borderRadius: '25px', textTransform: 'none', fontSize: '14px', fontWeight: '500', marginLeft: '6px' }} onClick={() => { setRolePopupColumnFilters({}); setRolePopupPage(0); }}>Clear All</button>
                  </div>
                </div>
              )}
              <table className="table mb-0" style={{ width: "100%", minWidth: "600px", borderCollapse: "collapse" }}>
                <thead style={{ backgroundColor: godrejStyles.secondary, color: "#fff", fontSize: "0.9rem", position: "sticky", top: 0, zIndex: 2, verticalAlign: "top", height: "36px", fontWeight: "400", whiteSpace: "nowrap" }}>
                  <tr>
                    <th style={{ height: "36px", padding: "8px 10px", fontWeight: "400", whiteSpace: "nowrap", position: "relative", textAlign: "center", width: "50px", backgroundColor: godrejStyles.secondary }}>
                    </th>
                    {[{key: "role_code", label: "Role Code"}, {key: "role_desc", label: "Role Description"}].map(col => (
                      <th key={col.key} onMouseEnter={() => setHoveredRoleCol(col.key)} onMouseLeave={() => setHoveredRoleCol(null)} style={{ height: "36px", padding: "8px 10px", fontWeight: "400", whiteSpace: "nowrap", position: "relative", textAlign: "left", backgroundColor: godrejStyles.secondary }}>
                        <span style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                          {col.label}
                          <img
                            src={chevronup}
                            alt="sort"
                            onClick={() => setRolePopupSort(prev => ({ key: col.key, direction: prev.key === col.key && prev.direction === "asc" ? "desc" : "asc" }))}
                            style={{ width: "12px", height: "12px", marginLeft: "5px", filter: "brightness(0) invert(1)", cursor: "pointer", transform: rolePopupSort.key === col.key && rolePopupSort.direction === "desc" ? "rotate(180deg)" : "none", opacity: hoveredRoleCol === col.key || rolePopupSort.key === col.key ? 1 : 0, transition: "opacity 0.2s" }}
                          />
                          <img
                            src={threedots}
                            alt="filter"
                            onClick={() => {
                              if (rolePopupFilter === col.key) {
                                setRolePopupFilter(null);
                              } else {
                                setRolePopupFilter(col.key);
                                setRolePopupFilterDraft(rolePopupColumnFilters[col.key] || { type: "Contains", value: "" });
                              }
                            }}
                            style={{ width: "14px", height: "14px", marginLeft: "5px", filter: "brightness(0) invert(1)", cursor: "pointer", opacity: hoveredRoleCol === col.key || rolePopupFilter === col.key || rolePopupColumnFilters[col.key] ? 1 : 0, transition: "opacity 0.2s" }}
                          />
                        </span>
                        {rolePopupFilter === col.key && (
                          <div style={{
                            position: "absolute", top: "36px", left: "0", zIndex: 1000,
                            background: "#fff", border: "1px solid #ccc", borderRadius: "4px",
                            padding: "12px", minWidth: "200px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                          }}>
                            <select
                              className="form-control"
                              value={rolePopupFilterDraft.type}
                              onChange={(e) => {
                                const draft = { ...rolePopupFilterDraft, type: e.target.value };
                                setRolePopupFilterDraft(draft);
                              }}
                              style={{ fontSize: "13px", marginBottom: "8px", appearance: "none", WebkitAppearance: "none", MozAppearance: "none", backgroundImage: `url(${chevrondown})`, backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center", backgroundSize: "12px 12px", paddingRight: "30px" }}
                            >
                              {["Contains", "Not Contains", "Equals", "Not Equals", "Starts With", "Ends With", "Blank", "Not Blank"].map(opt => (
                                <option key={opt}>{opt}</option>
                              ))}
                            </select>
                            {!["Blank", "Not Blank"].includes(rolePopupFilterDraft.type) && (
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Filter..."
                                value={rolePopupFilterDraft.value}
                                onChange={(e) => {
                                  const draft = { ...rolePopupFilterDraft, value: e.target.value };
                                  setRolePopupFilterDraft(draft);
                                }}
                                style={{ fontSize: "13px", marginBottom: "8px" }}
                              />
                            )}
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                              <span
                                onClick={() => {
                                  setRolePopupColumnFilters(prev => { const n = { ...prev }; delete n[col.key]; return n; });
                                  setRolePopupFilterDraft({ type: "Contains", value: "" });
                                  setRolePopupFilter(null);
                                  setRolePopupPage(0);
                                }}
                                style={{ cursor: "pointer", color: "#707070", fontSize: "12px", fontWeight: "600" }}
                              >RESET</span>
                              <span
                                onClick={() => {
                                  if (rolePopupFilterDraft.value || rolePopupFilterDraft.type === "Blank" || rolePopupFilterDraft.type === "Not Blank") {
                                    setRolePopupColumnFilters(prev => ({ ...prev, [col.key]: { ...rolePopupFilterDraft } }));
                                  }
                                  setRolePopupFilter(null);
                                  setRolePopupPage(0);
                                }}
                                style={{ cursor: "pointer", color: "#810055", fontSize: "12px", fontWeight: "600" }}
                              >APPLY</span>
                            </div>
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedRoles.length > 0 ? (
                    paginatedRoles.map((roleObj) => (
                      <tr 
                        key={roleObj.rm_role} 
                        onClick={() => setSelectedRoleForPopup(roleObj.rm_role)}
                        style={{ backgroundColor: selectedRoleForPopup === roleObj.rm_role ? '#F9F2F6' : '#fff', cursor: 'pointer', borderBottom: '1px solid #e0e0e0' }}
                        onMouseEnter={(e) => { if (selectedRoleForPopup !== roleObj.rm_role) e.currentTarget.style.backgroundColor = '#F9F2F6'; }}
                        onMouseLeave={(e) => { if (selectedRoleForPopup !== roleObj.rm_role) e.currentTarget.style.backgroundColor = '#fff'; }}
                      >
                        <td style={{ whiteSpace: "nowrap", textAlign: "center", padding: "8px 10px", borderBottom: "1px solid #e0e0e0" }}>
                          <div style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            border: `2px solid ${selectedRoleForPopup === roleObj.rm_role ? godrejStyles.primary : '#ccc'}`,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}>
                            {selectedRoleForPopup === roleObj.rm_role && (
                              <div style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: godrejStyles.primary
                              }}></div>
                            )}
                          </div>
                        </td>
                        <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderBottom: "1px solid #e0e0e0" }}>
                          {roleObj.rm_role}
                        </td>
                        <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderBottom: "1px solid #e0e0e0" }}>
                          {roleObj.rm_role_description || ''}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center py-4" style={{ color: godrejStyles.secondary }}>
                        {roleSearchTerm ? "No matching roles found" : "No roles found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer with Pagination and Add button */}
            <div style={{ padding: '10px 20px', flexShrink: 0, borderTop: '1px solid #eee' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '14px', color: '#707070' }}>Items per page</span>
                  <input 
                    min="1" max="10" type="number" 
                    value={rolePopupPageSize}
                    onChange={(e) => { const val = parseInt(e.target.value); if (val >= 1 && val <= 10) { setRolePopupPageSize(val); setRolePopupPage(0); } }}
                    style={{ width: '60px', height: '30px', border: 'none', borderBottom: '1px solid #000', borderRadius: '0', textAlign: 'center', fontSize: '14px', outline: 'none' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <button 
                    type="button" 
                    onClick={() => setRolePopupPage(rolePopupPage - 1)}
                    disabled={rolePopupPage === 0 || filteredRolesForPopup.length === 0}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', border: 'none', background: 'transparent', color: (rolePopupPage === 0 || filteredRolesForPopup.length === 0) ? '#ccc' : '#707070', cursor: (rolePopupPage === 0 || filteredRolesForPopup.length === 0) ? 'not-allowed' : 'pointer', fontSize: '14px' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor"><path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"></path></svg>
                    Previous
                  </button>
                  <div style={{ width: '1px', height: '30px', backgroundColor: '#e7e7e7' }}></div>
                  <button 
                    type="button" 
                    onClick={() => setRolePopupPage(rolePopupPage + 1)}
                    disabled={rolePopupPage >= rolePopupTotalPages() - 1 || filteredRolesForPopup.length === 0}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', border: 'none', background: 'transparent', color: (rolePopupPage >= rolePopupTotalPages() - 1 || filteredRolesForPopup.length === 0) ? '#ccc' : '#810055', cursor: (rolePopupPage >= rolePopupTotalPages() - 1 || filteredRolesForPopup.length === 0) ? 'not-allowed' : 'pointer', fontSize: '14px' }}
                  >
                    Next
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor"><path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"></path></svg>
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '14px', color: '#707070' }}>Total {filteredRolesForPopup.length} Records</span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button 
                  onClick={handleRoleSelect}
                  style={{
                    backgroundColor: godrejStyles.primary,
                    color: 'white',
                    border: 'none',
                    padding: '3px 15px',
                    borderRadius: '25px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    textTransform: 'none',
                    minWidth: '80px'
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Changes Popup */}
      {noChangesError && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1003
        }}>
          <div style={{
            backgroundColor: 'white', padding: '30px 40px', borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)', textAlign: 'center', minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>No Changes Found.</p>
            <button type="button" className="btn" onClick={() => setNoChangesError(false)} style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>OK</button>
          </div>
        </div>
      )}

      {/* Confirm Delete Popup */}
      {showConfirmDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1003
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>Are you sure you want to delete the user?</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
              <button type="button" className="btn" onClick={() => setShowConfirmDelete(null)} style={{ backgroundColor: 'transparent', color: '#707070', border: '1px solid #ccc', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>No</button>
              <button type="button" className="btn" onClick={confirmDelete} style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>Yes</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Update Popup */}
      {showConfirmUpdate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1003
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>Are you sure, do you want to update User ?</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
              <button 
                type="button" 
                className="btn" 
                onClick={() => setShowConfirmUpdate(false)} 
                style={{ backgroundColor: 'transparent', color: '#707070', border: '1px solid #ccc', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}
              >
                No
              </button>
              <button 
                type="button" 
                className="btn" 
                onClick={() => { setShowConfirmUpdate(false); handleSave(); }} 
                style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message Popup */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>{successMessage}</p>
            <button 
              type="button" 
              className="btn" 
              onClick={() => setSuccessMessage(null)} 
              style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Delete Error Popup */}
      {deleteError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>{deleteError}</p>
            <button 
              type="button" 
              className="btn" 
              onClick={() => setDeleteError(null)} 
              style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* View User Popup */}
      {viewUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '0',
            borderRadius: '0',
            width: '70%',
            maxWidth: '800px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #ccc', padding: '20px 20px 15px 20px' }}>
              <h2 style={{ color: '#333', fontSize: '18px', fontWeight: '500', margin: 0 }}>View User</h2>
              <button 
                onClick={() => setViewUser(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#707070',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ width: '100%', padding: '15px 20px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', margin: 0, padding: 0 }}>
                {[
                  ["Status", viewUser.um_status],
                  ["User ID", viewUser.um_user_id],
                  ["User Name", viewUser.um_user_name],
                  ["Salary Code", viewUser.um_salary_code],
                  ["Role", getRoleDescription(viewUser.um_role_name)],
                  ["Mobile No.", maskPII(viewUser.um_mobile_no)],
                  ["Telephone No.", maskPII(viewUser.um_telephone_no)],
                  ["Email ID", viewUser.um_email],
                  ["Employee Band", viewUser.um_employee_band],
                  ["Designation", viewUser.um_designation],
                  ["Department", viewUser.um_department],
                  ["Inserted By", resolveUserName(viewUser.um_inserted_by)],
                  ["Inserted On", formatDateTime(viewUser.um_inserted_on)],
                  ["Modified By", resolveUserName(viewUser.um_modified_by)],
                  ["Modified On", formatDateTime(viewUser.um_modified_on)],
                ].map(([label, value]) => (
                  <div style={{ width: '33.33%', padding: '0 10px', paddingBottom: '12px' }} key={label}>
                    <label style={{ color: '#707070', marginBottom: '2px', display: 'block', fontSize: '12px' }}>{label}</label>
                    <div style={{
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '10px 14px',
                      fontSize: '14px',
                      width: '100%',
                      height: '38px',
                      backgroundColor: '#f9f9f9',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden'
                    }}
                      title={value || '-'}
                    >
                      <span style={{ whiteSpace: 'nowrap', overflow: 'auto', width: '100%', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>{value || '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button type="button" className="btn" onClick={() => setViewUser(null)} style={{ backgroundColor: 'transparent', color: '#707070', border: '1px solid #ccc', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        {Object.keys(columnFilters).length > 0 && (
          <div className="filterList" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', padding: '5px 10px' }}>
            {Object.entries(columnFilters).map(([colKey, filter]) => {
              const col = columns.find(c => c.key === colKey);
              return (
                <div key={colKey} className="filter-col pr-0 me-2 mb-2" style={{ display: 'flex', alignItems: 'center', backgroundColor: '#F9F2F6', borderRadius: '25px', padding: '2px 8px', fontSize: '13px' }}>
                  <span className="ps-2 btn-text">{col?.label || colKey} : {filter.type === 'Blank' || filter.type === 'Not Blank' ? filter.type : filter.value}</span>
                  <div onClick={() => resetColumnFilter(colKey)} style={{ cursor: 'pointer', marginLeft: '4px' }}>
                    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 16 16" height="25" width="25" xmlns="http://www.w3.org/2000/svg"><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"></path></svg>
                  </div>
                </div>
              );
            })}
            <div>
              <button type="button" className="btn" style={{ backgroundColor: 'white', color: '#810055', border: '1px solid #810055', padding: '3px 15px', borderRadius: '25px', textTransform: 'none', fontSize: '14px', fontWeight: '500', marginLeft: '6px' }} onClick={() => { setColumnFilters({}); setCurrentPage(0); }}>Clear All</button>
            </div>
          </div>
        )}
        <div style={{ maxHeight: "400px", overflowY: "scroll", overflowX: "scroll", padding: "0px 0px 10px 10px", boxSizing: "border-box", backgroundColor: "#fff", marginTop: "5px" }}>
          <table className="table table-hover mb-0" style={{ width: "100%", minWidth: "2420px", borderCollapse: "separate", borderSpacing: "0 3px" }}>
            <thead style={{ backgroundColor: godrejStyles.secondary, color: "#fff", fontSize: "0.9rem", position: "sticky", top: 0, zIndex: 2, verticalAlign: "top", height: "36px", fontWeight: "400", whiteSpace: "nowrap" }}>
              <tr>
                {columns.map(col => (
                  <th key={col.key} onMouseEnter={() => setHoveredCol(col.key)} onMouseLeave={() => setHoveredCol(null)} style={{ height: "36px", padding: "8px 10px", fontWeight: "400", whiteSpace: "nowrap", position: "relative", textAlign: "left", width: columnWidths[col.key] ? `${columnWidths[col.key]}px` : "auto", backgroundColor: godrejStyles.secondary, boxShadow: "0 -4px 0 0 #fff" }}>
                    <span
                      style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                    >
                      {col.label}
                      <img
                        src={chevronup}
                        alt="sort"
                        onClick={() => handleSort(col.key)}
                        style={{ width: "12px", height: "12px", marginLeft: "5px", filter: "brightness(0) invert(1)", cursor: "pointer", transform: sortConfig.key === col.key && sortConfig.direction === "desc" ? "rotate(180deg)" : "none", opacity: hoveredCol === col.key || sortConfig.key === col.key ? 1 : 0, transition: "opacity 0.2s" }}
                      />
                      <img
                        src={threedots}
                        alt="filter"
                        onClick={() => {
                          if (activeFilter === col.key) {
                            setActiveFilter(null);
                          } else {
                            setActiveFilter(col.key);
                            setFilterDraft(columnFilters[col.key] || { type: "Contains", value: "" });
                          }
                        }}
                        style={{ width: "14px", height: "14px", marginLeft: "5px", filter: "brightness(0) invert(1)", cursor: "pointer", opacity: hoveredCol === col.key || activeFilter === col.key || columnFilters[col.key] ? 1 : 0, transition: "opacity 0.2s" }}
                      />
                    </span>
                    {activeFilter === col.key && (
                      <div style={{
                        position: "absolute", top: "36px", left: "0", zIndex: 1000,
                        background: "#fff", border: "1px solid #ccc", borderRadius: "4px",
                        padding: "12px", minWidth: "200px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                      }}>
                        <select
                          className="form-control"
                          value={filterDraft.type}
                          onChange={(e) => updateFilterLive(col.key, { ...filterDraft, type: e.target.value })}
                          style={{ fontSize: "13px", marginBottom: "8px", appearance: "none", WebkitAppearance: "none", MozAppearance: "none", backgroundImage: `url(${chevrondown})`, backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center", backgroundSize: "12px 12px", paddingRight: "30px" }}
                        >
                          {["Contains", "Not Contains", "Equals", "Not Equals", "Starts With", "Ends With", "Blank", "Not Blank"].map(opt => (
                            <option key={opt}>{opt}</option>
                          ))}
                        </select>
                        {!["Blank", "Not Blank"].includes(filterDraft.type) && (
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Filter..."
                            value={filterDraft.value}
                            onChange={(e) => updateFilterLive(col.key, { ...filterDraft, value: e.target.value })}
                            style={{ fontSize: "13px", marginBottom: "8px" }}
                          />
                        )}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                          <span
                            onClick={() => resetColumnFilter(col.key)}
                            style={{ cursor: "pointer", color: "#707070", fontSize: "12px", fontWeight: "600" }}
                          >RESET</span>
                          <span
                            onClick={() => applyColumnFilter(col.key)}
                            style={{ cursor: "pointer", color: "#810055", fontSize: "12px", fontWeight: "600" }}
                          >APPLY</span>
                        </div>
                      </div>
                    )}
                    <div
                      onMouseDown={(e) => handleMouseDown(e, col.key)}
                      style={{ position: "absolute", right: 0, top: "25%", height: "50%", width: "2px", backgroundColor: "#a0a0a0", cursor: "col-resize" }}
                    ></div>
                  </th>
                ))}
                <th style={{ height: "36px", padding: "8px 10px", fontWeight: "400", whiteSpace: "nowrap", textAlign: "left", backgroundColor: godrejStyles.secondary, position: "sticky", right: 0, zIndex: 3 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                (filteredUsers.slice(currentPage * pageSize, (currentPage + 1) * pageSize))
                  .map(user => (
                    <tr key={user.um_id} style={{ border: "1px solid #e0e0e0", backgroundColor: "#fff" }}>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", border: "1px solid #e0e0e0", borderRight: "none" }}>
                        <div style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "6px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          backgroundColor: user.um_status?.toLowerCase() === 'active' ? 'rgba(51, 192, 55, 0.1)' : 'rgba(112, 112, 112, 0.1)'
                        }}>
                          <img 
                            src={user.um_status?.toLowerCase() === 'active' ? require('../../assets/img/check-circle-fill.svg').default : require('../../assets/img/x-circle-fill.svg').default}
                            alt={user.um_status}
                            style={{
                              width: "16px",
                              height: "16px",
                              filter: user.um_status?.toLowerCase() === 'active' 
                                ? 'brightness(0) saturate(100%) invert(35%) sepia(85%) saturate(1352%) hue-rotate(87deg) brightness(104%) contrast(97%)'
                                : 'brightness(0) saturate(100%) invert(45%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)'
                            }}
                          />
                          <span style={{
                            color: user.um_status?.toLowerCase() === 'active' ? '#198754' : '#707070',
                            fontWeight: "500",
                            fontSize: "13px"
                          }}>
                            {user.um_status}
                          </span>
                        </div>
                      </td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{user.um_user_id}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{user.um_user_name}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{user.um_salary_code}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{getRoleDescription(user.um_role_name)}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{user.um_email}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{maskPII(user.um_mobile_no)}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{maskPII(user.um_telephone_no)}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{user.um_employee_band}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{user.um_designation}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{user.um_department}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{user.um_inserted_by}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{formatDateTime(user.um_inserted_on)}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{user.um_modified_by}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>{formatDateTime(user.um_modified_on)}</td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", border: "1px solid #e0e0e0", borderLeft: "none", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}>
                        <button onClick={() => { const u = users.find(usr => usr.um_id === user.um_id); setViewUser(u); }} className="btn btn-sm me-1" style={{ background: "none", border: "none", padding: 0, boxShadow: "none" }}>
                          <img src={eyeimg} alt="View" title="View" style={{ width: "20px", height: "20px", cursor: "pointer", filter: "invert(45%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)" }} />
                        </button>
                        <button onClick={() => handleEdit(user)} className="btn btn-sm me-1" style={{ background: "none", border: "none", padding: 0, boxShadow: "none" }}>
                          <img src={editimg} alt="Edit" title="Edit" style={{ width: "20px", height: "20px", cursor: "pointer" }} />
                        </button>
                        <button onClick={() => handleDelete(user.um_id)} className="btn btn-sm" style={{ background: "none", border: "none", padding: 0, boxShadow: "none" }}>
                          <img src={deleteimg} alt="Delete" title="Delete" style={{ width: "20px", height: "20px", cursor: "pointer", filter: "invert(45%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)" }} />
                        </button>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan="16" className="text-center py-4" style={{ color: godrejStyles.secondary }}>{searchTerm ? "No matching records found" : "No record found"}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '20px', backgroundColor: '#fff' }}>
          <div style={{ padding: '15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '14px', color: '#707070' }}>Items per page</span>
              <input 
                min="1" 
                max="10"
                type="number" 
                value={pageSize}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val >= 1 && val <= 10) {
                    setPageSize(val);
                    setCurrentPage(0);
                  }
                }}
                style={{
                  width: '60px',
                  height: '30px',
                  border: 'none',
                  borderBottom: '1px solid #000',
                  borderRadius: '0',
                  textAlign: 'center',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <button 
                type="button" 
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 0 || filteredUsers.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'transparent',
                  color: (currentPage === 0 || filteredUsers.length === 0) ? '#ccc' : '#707070',
                  cursor: (currentPage === 0 || filteredUsers.length === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor">
                  <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"></path>
                </svg>
                Previous
              </button>
              <div style={{ width: '1px', height: '30px', backgroundColor: '#e7e7e7' }}></div>
              <button 
                type="button" 
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= numberOfPages() - 1 || filteredUsers.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'transparent',
                  color: (currentPage >= numberOfPages() - 1 || filteredUsers.length === 0) ? '#ccc' : '#810055',
                  cursor: (currentPage >= numberOfPages() - 1 || filteredUsers.length === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Next
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor">
                  <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"></path>
                </svg>
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '14px', color: '#707070' }}>You are on Page No.</span>
                <select 
                  value={filteredUsers.length > 0 ? currentPage + 1 : 1}
                  onChange={(e) => setCurrentPage(parseInt(e.target.value) - 1)}
                  disabled={filteredUsers.length === 0}
                  style={{
                    padding: '5px 10px',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    minWidth: '60px',
                    backgroundColor: 'transparent',
                    outline: 'none'
                  }}
                >
                  {filteredUsers.length > 0 ? (
                    Array.from({ length: numberOfPages() }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))
                  ) : (
                    <option value={1}>1</option>
                  )}
                </select>
              </div>
              <span style={{ fontSize: '14px', color: '#707070' }}>Total {filteredUsers.length} Records</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserMaster;